import React, { useContext, useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { UserContext } from "../../CheckoutPage";
import ReactGA from "react-ga4";
import { trackData } from "../../../../Utils/attribution/CheckoutAttribution";
import { useToast } from "../../../../contexts/ToastContext";
import {
  sendOtpAPIPFH,
  verifyOtpPFHAPI,
  getAddressFromPincodeAPI,
  verifyOtpNewhub,
  sendOtpNewhub,
  addPfhStudent,
  searchSchools,
  trackCheckoutInitiated,
  PhoneSubmitResponseData,
} from "../../../../Utils/CheckoutAPIs/CheckoutAPIs";
import {
  getDeviceId,
  rotateDeviceId,
  isDuplicateDeviceSessionError,
} from "../../../../Utils/deviceId";
import MobileIcon from "../../../../Assets/svgs/MobileIcon.png";
import "./V2StudentDetails.css";

/**
 * V2StudentDetails Component
 *
 * Features Added from Original Design:
 * ✅ OTP Verification System - ACTIVE (inline layout)
 * ✅ PFH (Referred Student) Verification - Available (commented out)
 * ✅ Advanced Analytics Tracking - ACTIVE
 * ✅ Mobile number verification with 4-digit OTP
 * ✅ Resend OTP functionality with 30-second timer
 * ✅ Form validation matching original FormCluster
 * ✅ Pincode field with address auto-fetch functionality
 *
 * Grid Layout Structure:
 * Row 1: Student Name | Email
 * Row 2: Mobile Number (with inline OTP) | School Name
 * Row 3: Grade Dropdown | Pincode
 * [Optional Row: School City (for non-individual registration)]
 */

// Shown when a household (phone) already has 2 current-year student profiles (registered or
// not) — the per-family maximum — and there's no existing profile left to register. The
// mobile-number field renders this specially via `.includes(PROFILE_LIMIT_MATCH)`.
const PROFILE_LIMIT_MATCH = "2 student profiles";
const PROFILE_LIMIT_MESSAGE =
  "Please use a different number. You already have 2 student profiles in your account.";

/**
 * Normalizes a `?returnuserphone=` value to a bare 10-digit Indian mobile number, tolerating
 * whatever prefix/formatting the link-generating system (WebEngage, marketing, etc.) used:
 * a leading 0, country code (91 or +91), or separator dashes/spaces. Preserves the `$5092`
 * test-bypass suffix (stripped before digit-extraction, reappended after) so
 * "7488165460$5092"-style test links keep working. Not used for the Dubai/international path —
 * dubaiMobileValidation already tolerates its own prefixes directly in its regex.
 */
function normalizeReturnPhoneParam(raw: string): string {
  const bypassMatch = raw.match(/\$5092$/);
  const bypassSuffix = bypassMatch ? bypassMatch[0] : "";
  const withoutSuffix = bypassSuffix ? raw.slice(0, -bypassSuffix.length) : raw;
  const digitsOnly = withoutSuffix.replace(/\D/g, "");
  return digitsOnly.slice(-10) + bypassSuffix;
}

const V2StudentDetails: React.FC = () => {
  const userContext = useContext(UserContext);
  const { showToast } = useToast();

  // Basic state initialization without complex logic
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  // Raw mobile input (may include $5092 test suffix) — kept separate so the
  // field displays what the user typed while the cleaned number goes to context/APIs.
  const [rawMobileInput, setRawMobileInput] = useState("");

  // OTP Verification System
  const [pfh, setPfh] = useState("");
  const [otp, setOtp] = useState("");
  const [isOtpFieldVisible, setIsOtpFieldVisible] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const [isResendEnabled, setIsResendEnabled] = useState(false);
  const [isOtpValid, setIsOtpValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Set to true when the email field loses focus with a valid value — gates the relay
  const [emailConfirmed, setEmailConfirmed] = useState(false);

  // CUID — temporary roll number issued at verify-otp time; becomes final on payment
  const [registrationCuid, setRegistrationCuid] = useState<string>(
    () => sessionStorage.getItem("registration_cuid") ?? ""
  );
  // Guard: fire checkout_initiated relay only once per checkout attempt
  const checkoutInitiatedFired = useRef(false);

  // Pending verify-otp response (stores tokens for stadia redirect)
  const [pendingVerifyOtpRes, setPendingVerifyOtpRes] = useState<any>(null);

  // Re-register returning student modal (previous-year completed profiles, no 2026 profile yet)
  const [showReregisterModal, setShowReregisterModal] = useState(false);
  const [reregisterCandidates, setReregisterCandidates] = useState<any[]>([]);

  // Second student modal (1 completed registration — show existing student, confirm before adding)
  const [showSecondStudentModal, setShowSecondStudentModal] = useState(false);
  const [existingStudentName, setExistingStudentName] = useState("");

  // PFH unregistered profile selection modal
  const [showPfhSelectModal, setShowPfhSelectModal] = useState(false);
  const [pfhUnregisteredProfiles, setPfhUnregisteredProfiles] = useState<any[]>([]);
  const [pfhRegisteredProfiles, setPfhRegisteredProfiles] = useState<any[]>([]);
  const [pfhTotalProfileCount, setPfhTotalProfileCount] = useState(0);
  // Whether this household can create one more current-year student profile (cap is 2 per
  // phone, any state). Backend-driven (`can_add_profile`) when present; falls back to counting
  // current-year profiles. Registering an EXISTING profile is never blocked by this.
  const [canAddProfile, setCanAddProfile] = useState(true);

  // New child details modal state
  const [showNewChildModal, setShowNewChildModal] = useState(false);
  const [newChild, setNewChild] = useState({ name: "", school: "", schoolId: null as number | null, grade: "" });
  const [newChildErrors, setNewChildErrors] = useState({ name: "", school: "", grade: "" });
  const [schoolQuery, setSchoolQuery] = useState("");
  const [schoolResults, setSchoolResults] = useState<{ id: number; name: string; display_label: string; city: string; state: string }[]>([]);
  const [schoolSearchLoading, setSchoolSearchLoading] = useState(false);
  const [showSchoolDropdown, setShowSchoolDropdown] = useState(false);
  const [isAddingStudent, setIsAddingStudent] = useState(false);

  // PFH-specific states
  const [isPfhUser, setIsPfhUser] = useState(false);
  const [pfhVerificationFailed, setPfhVerificationFailed] = useState(false);
  const [showPfhSuccessPopup, setShowPfhSuccessPopup] = useState(false);
  const [pfh2025BookOffer, setPfh2025BookOffer] = useState(false);

  // ?returnuserphone=<phone> — prefill mobile number for a returning user and
  // auto-trigger the phone-submit call the manual "Send OTP" flow normally does.
  const [returnUserPhone, setReturnUserPhone] = useState<string | null>(null);
  const returnUserPhoneAutoTriggered = useRef(false);
  // phone-submit's is_returning_customer — unused for now (false behaves exactly as before);
  // captured so the "returning customer" flow can branch on it later.
  const [isReturningCustomer, setIsReturningCustomer] = useState(false);

  // Pincode and address fetching states
  const [pincode, setPincode] = useState("");
  const [pincodeLoading, setPincodeLoading] = useState(false);

  // Grade state
  const [grade, setGrade] = useState(0);

  // Validation states
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    mobileNumber: "",
    otp: "", // OTP error
    sendOtpError: "", // OTP sending error
    pincode: "", // Pincode error
  });

  // Analytics tracking - matching original FormCluster implementation with OTP events
  const [trackedFields, setTrackedFields] = useState<Record<string, boolean>>(
    () => {
      const saved = sessionStorage.getItem("trackedFormFields");
      return saved
        ? JSON.parse(saved)
        : {
            mobile: false,
            name: false,
            otp_sent: false,
            otp_verified: false,
            pincode: false,
          };
    }
  );

  // Validation patterns — allow optional $5092 test suffix (e.g. 9895866303$5092)
  const mobileValidation = /^[1-9][0-9]{9}(\$5092)?$/;
  const dubaiMobileValidation =
    /^((\+971|971|0)?(50|51|52|54|55|56|58|4))\d{7}(\$5092)?$/;

  // Strip the $5092 test suffix before sending to any API
  const normalisePhone = (phone: string) => phone.replace(/\$5092$/, "");
  const otpValidation = /^\d{4}$/; // OTP validation
  const pincodeValidation = /^\d{6}$/; // Pincode validation

  // Save tracking data to sessionStorage
  useEffect(() => {
    sessionStorage.setItem("trackedFormFields", JSON.stringify(trackedFields));
  }, [trackedFields]);

  // Initialize form values from sessionStorage and context after component mounts
  useEffect(() => {
    try {
      const savedName = sessionStorage.getItem("name");
      const savedEmail = sessionStorage.getItem("email");
      const savedPfh = sessionStorage.getItem("pfh");
      const savedPincode = sessionStorage.getItem("pincode");

      if (savedName && savedName !== "null") setName(savedName);
      if (savedEmail && savedEmail !== "null") setEmail(savedEmail);
      if (savedPfh && savedPfh !== "null") {
        setPfh(savedPfh);
        if (savedPfh === "true") {
          setIsPfhUser(true);
          console.log("🎯 PFH user detected - enabling PFH verification flow");
        }
      }
      if (savedPincode && savedPincode !== "null" && savedPincode !== "560076") {
        setPincode(savedPincode);
      } else {
        setPincode("");
      }

      // Grade: check sessionStorage first, then URL param
      const savedGrade = sessionStorage.getItem("grade");
      const urlParams = new URLSearchParams(window.location.search);
      const utmGrade = urlParams.get("grade");
      if (savedGrade && savedGrade !== "0" && savedGrade !== "null") {
        const g = parseInt(savedGrade, 10);
        if (!isNaN(g) && g >= 5 && g <= 12) {
          setGrade(g);
          userContext.setUserDetails((prev: any) => ({ ...prev, grade: g }));
        }
      } else if (utmGrade) {
        const g = parseInt(utmGrade, 10);
        if (!isNaN(g) && g >= 5 && g <= 12) {
          setGrade(g);
          sessionStorage.setItem("grade", String(g));
          userContext.setUserDetails((prev: any) => ({ ...prev, grade: g }));
        }
      }

      // ?returnuserphone=<phone> — prefill Mobile Number for a returning user. Links are
      // generated by other systems (WebEngage, marketing, etc.) that may include a leading 0,
      // country code (91/+91), or separator dashes/spaces — normalize to the last 10 digits
      // before validating so those variants prefill correctly instead of silently failing
      // mobileValidation's strict 10-digit pattern. Only for the India path: Dubai numbers have
      // a different length/structure and dubaiMobileValidation already tolerates its own
      // prefixes (+971/971/0) directly in the regex.
      const returnPhoneParamRaw = urlParams.get("returnuserphone");
      const returnPhoneParam =
        returnPhoneParamRaw && !userContext.backendSchool?.isInternational
          ? normalizeReturnPhoneParam(returnPhoneParamRaw)
          : returnPhoneParamRaw;
      if (returnPhoneParam) {
        const isValidReturnPhone = userContext.backendSchool?.isInternational
          ? dubaiMobileValidation.test(returnPhoneParam)
          : mobileValidation.test(returnPhoneParam);
        if (isValidReturnPhone) {
          // Same OTP-state reset handleMobileChange does on any mobile-number change — this
          // path bypasses that handler, but a tab can still carry an isOtpVerified=true cached
          // from earlier in the same session (CheckoutPage restores it from sessionStorage on
          // mount) for a DIFFERENT phone than the one this link is prefilling.
          if (returnPhoneParam !== userContext.userDetails?.mobileNumber) {
            setOtp("");
            setIsOtpValid(false);
            setIsOtpFieldVisible(false);
            setIsResendEnabled(false);
            setResendTimer(30);
            userContext.setIsOtpVerified(false);
            setErrors((prev) => ({ ...prev, otp: "", sendOtpError: "" }));
          }
          userContext.setUserDetails((prev: any) => ({
            ...prev,
            mobileNumber: returnPhoneParam,
          }));
          sessionStorage.setItem("mobileNumber", returnPhoneParam);
          setReturnUserPhone(returnPhoneParam);
        }
      }
    } catch (error) {
      console.error("Error initializing form values:", error);
      showToast("Failed to initialize form. Please refresh the page.", "error");
    }
  }, []);


  // Persist CUID helper — always keep state + sessionStorage in sync
  const persistCuid = (cuid: string) => {
    if (!cuid) return;
    setRegistrationCuid(cuid);
    sessionStorage.setItem("registration_cuid", cuid);
  };

  // Fire checkout_initiated relay once — on blur OR after 2s debounce while still typing
  const fireCheckoutInitiated = () => {
    if (checkoutInitiatedFired.current) return;
    const phone = userContext.userDetails?.mobileNumber ?? "";
    const cuid = registrationCuid || sessionStorage.getItem("registration_cuid") || "";
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
    if (!cuid || !name || !phone || !email || !emailValid) return;

    checkoutInitiatedFired.current = true;
    trackCheckoutInitiated({
      cuid,
      name,
      email,
      phone: phone.slice(-10),
      grade: grade || undefined,
      is_pfh_user: !!isPfhUser,
    }).catch(() => {
      checkoutInitiatedFired.current = false;
    });
  };

  // 2-second debounce while the user is still typing in the email field
  useEffect(() => {
    if (checkoutInitiatedFired.current || emailConfirmed) return;
    const timer = setTimeout(fireCheckoutInitiated, 2000);
    return () => clearTimeout(timer);
  }, [email, name, registrationCuid, emailConfirmed]);

  // Analytics tracking function - matching original FormCluster
  const trackFieldCompletion = (fieldName: string, isValid: boolean) => {
    if (isValid && !trackedFields[fieldName]) {
      trackData(`5678_checkout_${fieldName}`);
      ReactGA.event({
        category: "Form_Completion",
        action: `5678_checkout_${fieldName}`,
        label: "Checkout_Form",
      });
      setTrackedFields((prev) => ({
        ...prev,
        [fieldName]: true,
      }));
    }
  };

  // Auto-fetch address from pincode
  const fetchAddressFromPincode = async (pincodeValue: string) => {
    if (pincodeValue.length === 6) {
      setPincodeLoading(true);
      try {
        const response = await getAddressFromPincodeAPI(pincodeValue);
        if (
          response &&
          response.data &&
          response.data.city &&
          response.data.state
        ) {
          // Update userDetails with fetched city and state
          userContext.setUserDetails((prevUserDetails: any) => ({
            ...prevUserDetails,
            city: response.data.city,
            state: response.data.state,
            pincode: pincodeValue,
          }));

          // Track pincode completion
          trackFieldCompletion("pincode", true);
          showToast("Address details fetched successfully!", "success");
        }
      } catch (error) {
        console.error("Error fetching address:", error);
        showToast(
          "Failed to fetch address from pincode. Please enter manually.",
          "warning"
        );
        setErrors((prev) => ({
          ...prev,
          pincode: "Unable to fetch address. Please enter manually.",
        }));
      } finally {
        setPincodeLoading(false);
      }
    }
  };

  // Timer for resend OTP
  const startResendTimer = () => {
    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setIsResendEnabled(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Send OTP
  const sendOtp = async () => {
    const isValidMobile = userContext.backendSchool?.isInternational
      ? dubaiMobileValidation.test(userContext.userDetails?.mobileNumber || "")
      : mobileValidation.test(userContext.userDetails?.mobileNumber || "");

    if (!isValidMobile) {
      setErrors((prev) => ({
        ...prev,
        sendOtpError: "Please enter a valid mobile number first",
      }));
      return;
    }

    setIsLoading(true);
    try {
      const phone = normalisePhone(userContext.userDetails?.mobileNumber || "");
      const response = isPfhUser
        ? await sendOtpAPIPFH(phone)
        : await sendOtpNewhub(phone, getDeviceId());

      if (!isPfhUser && response.status === "Success") {
        const data = response.data as unknown as PhoneSubmitResponseData;
        const returning = !!data?.is_returning_customer;
        setIsReturningCustomer(returning);
        if (returning) {
          // Deliberately NOT the same "nfo2024" sessionStorage key a ?nfo2024=true marketing
          // link sets: that key also drives verifyOtp()'s choice of verification endpoint
          // (legacy verifyOtpPFHAPI vs. the newhub verifyOtpNewhub this OTP was actually sent
          // through) — reusing it here would silently reroute EVERY returning customer's OTP
          // verify through the legacy PFH-style endpoint, which skips newhub-only steps
          // (CUID reservation, token storage, stadiaAutoLogin, and the "already has 2
          // registrations this year" duplicate-registration check). This flag is
          // display-pricing-only; own key persists it across a reload the same way nfo2024
          // does for its own population, without affecting verify-routing.
          sessionStorage.setItem("returningCustomerPricing", "true");
          userContext.setNfo2024Eligible(true);
        }
        // Returning, PFH (₹299 PFH 2026 / ₹199) or PFH 2025 (₹499 + free guidebook): show this
        // phone's price now rather than only after OTP verification (CheckoutPage refetches).
        if (returning || data?.is_pfh_customer || data?.is_pfh_2025_offer) {
          userContext.setEarlyPricePhone(phone);
        }
      }

      if (response.status === "Success") {
        setIsOtpFieldVisible(true);
        setOtp("");
        setIsOtpValid(false);
        setIsResendEnabled(false);
        setResendTimer(30);
        startResendTimer();
        setErrors((prev) => ({ ...prev, sendOtpError: "" }));
        showToast("OTP sent successfully! Check your mobile for the 4-digit code.", "success");

        if (trackedFields.mobile) {
          trackData(isPfhUser ? "5678_pfh_send_otp_button" : "5678_checkout_otp_sent");
          ReactGA.event({
            category: isPfhUser ? "PFH_Verification" : "Form_Completion",
            action: isPfhUser ? "5678_pfh_send_otp_button" : "5678_checkout_otp_sent",
            label: isPfhUser ? "Discount_Flow" : "Checkout_Form",
          });
          setTrackedFields((prev) => ({ ...prev, otp_sent: true }));
        }
      } else {
        setErrors((prev) => ({ ...prev, sendOtpError: "Error sending OTP, Please try again" }));
      }
    } catch {
      setErrors((prev) => ({ ...prev, sendOtpError: "Error sending OTP, Please try again" }));
    } finally {
      setIsLoading(false);
    }
  };

  // Once the mobile number has been prefilled from ?returnuserphone, auto-trigger the
  // same phone-submit call the "Send OTP" button normally fires on manual entry.
  useEffect(() => {
    if (!returnUserPhone) return;
    if (returnUserPhoneAutoTriggered.current) return;
    if (userContext.userDetails?.mobileNumber !== returnUserPhone) return;

    returnUserPhoneAutoTriggered.current = true;
    sendOtp();
  }, [returnUserPhone, userContext.userDetails?.mobileNumber]);

  // Verify OTP
  const verifyOtp = async () => {
    setErrors((prev) => ({ ...prev, otp: "" }));
    setIsLoading(true);
    const nfo2024Param = sessionStorage.getItem("nfo2024") === "true";
    const phone = normalisePhone(userContext.userDetails?.mobileNumber || "");

    try {
      if (isPfhUser || nfo2024Param) {
        // PFH/NFO2024 flow
        const response = await verifyOtpPFHAPI(phone, otp);
        if (response.status !== "Success") {
          setErrors((prev) => ({ ...prev, otp: "Invalid OTP. Please try again." }));
          return;
        }

        // Household already has 2 current-year student profiles (IR only). This path always
        // creates a new profile → block with the same UI as the newhub path below; don't
        // proceed to item selection / payment.
        if (typeof response.data === "object" && response.data?.profileLimitReached) {
          setErrors((prev) => ({
            ...prev,
            mobileNumber: PROFILE_LIMIT_MESSAGE,
          }));
          setIsOtpFieldVisible(false);
          return;
        }

        userContext.setIsOtpVerified(true);
        setIsOtpFieldVisible(false);

        const pfhVerified = (typeof response.data === "object" && response.data?.pfh) || false;
        const nfo2024Verified = (typeof response.data === "object" && response.data?.nfo2024) || false;

        const pfh2025Offer = (typeof response.data === "object" && response.data?.pfh2025BookOffer) || false;

        userContext.setPfhVerified(pfhVerified);
        userContext.setNfo2024Verified(nfo2024Verified);
        sessionStorage.setItem("pfhVerified", pfhVerified.toString());
        sessionStorage.setItem("nfo2024Verified", nfo2024Verified.toString());
        sessionStorage.setItem("pfh2025BookOffer", pfh2025Offer.toString());
        setPfh2025BookOffer(pfh2025Offer);

        if (pfh2025Offer) {
          // PFH 2025 households pay the regular price and get a free physical guidebook —
          // not a discount, so don't promise one.
          setShowPfhSuccessPopup(true);
          showToast("Verification successful! You'll get a free physical guidebook with your registration.", "success");
        } else if (pfhVerified) {
          setShowPfhSuccessPopup(true);
          showToast("PFH verification successful! You're eligible for the discount.", "success");
        } else if (nfo2024Verified) {
          showToast("NFO2024 verification successful! You're eligible for the discount.", "success");
        } else {
          setPfhVerificationFailed(true);
          showToast("Verification successful! Standard pricing applies.", "warning");
        }

        trackData("5678_pfh_verify_otp_button");
        ReactGA.event({ category: "PFH_Verification", action: "5678_pfh_verify_otp_button", label: "Discount_Flow" });
      } else {
        // Regular flow: newhub /auth/verify-otp with device_id
        let deviceId = getDeviceId();
        let res = await verifyOtpNewhub(phone, otp, deviceId);

        // Rotate device_id and retry once on duplicate session error
        if (res.status !== "Success" && isDuplicateDeviceSessionError(res.message ?? "")) {
          deviceId = rotateDeviceId();
          res = await verifyOtpNewhub(phone, otp, deviceId);
        }

        if (res.status !== "Success") {
          setErrors((prev) => ({ ...prev, otp: "Invalid OTP. Please try again." }));
          return;
        }

        // Profile-based routing
        const allProfiles: any[] = res.data?.profiles ?? [];
        // Profiles registered for 2026 (current cycle) — cannot register again.
        const profiles2026 = allProfiles.filter((p: any) => p.olympiad_year != null && p.olympiad_year >= 2026);
        // Unregistered = not yet completed AND not already in the 2026 cycle.
        const unregisteredProfiles = allProfiles.filter(
          (p: any) => !p.registration_completed && (p.olympiad_year == null || p.olympiad_year < 2026)
        );

        // Returning student: has completed profiles from a previous year, none for 2026 yet.
        // Show a "re-register same student?" popup before falling into the standard new-user flow.
        const previousCompletedProfiles = allProfiles.filter(
          (p: any) => p.registration_completed && (p.olympiad_year == null || p.olympiad_year < 2026)
        );

        // Household cap: 2 current-year student PROFILES per phone (registered or not). Prefer
        // the backend's `can_add_profile`; fall back to counting current-year profiles of any
        // state. At the cap only "add a new child" is hidden — an existing profile can still be
        // picked and registered. Block outright only when there's nothing existing to pick.
        const canAdd =
          typeof res.data?.can_add_profile === "boolean"
            ? res.data.can_add_profile
            : profiles2026.length < 2;
        setCanAddProfile(canAdd);
        const hasExistingProfileToRegister =
          unregisteredProfiles.length > 0 ||
          (previousCompletedProfiles.length > 0 && profiles2026.length === 0);
        if (!canAdd && !hasExistingProfileToRegister) {
          setErrors((prev) => ({
            ...prev,
            mobileNumber: PROFILE_LIMIT_MESSAGE,
          }));
          setIsOtpFieldVisible(false);
          return;
        }
        if (previousCompletedProfiles.length > 0 && profiles2026.length === 0) {
          if (res.data?.token) sessionStorage.setItem("newhubToken", res.data.token);
          if (res.data?.refresh_token) sessionStorage.setItem("newhubRefreshToken", res.data.refresh_token);
          setPendingVerifyOtpRes(res);
          setReregisterCandidates(previousCompletedProfiles);
          setIsOtpFieldVisible(false);
          setShowReregisterModal(true);
          return;
        }

        // 1 completed profile, no unregistered — PFH user can still add a second child
        // Show the existing student first, ask them to confirm before opening the new child form
        if (allProfiles.length === 1 && unregisteredProfiles.length === 0 && res.data?.is_pfh_customer) {
          if (res.data?.token) sessionStorage.setItem("newhubToken", res.data.token);
          if (res.data?.refresh_token) sessionStorage.setItem("newhubRefreshToken", res.data.refresh_token);
          setPendingVerifyOtpRes(res);
          setExistingStudentName(allProfiles[0].name);
          setIsOtpFieldVisible(false);
          setShowSecondStudentModal(true);
          return;
        }

        // Unregistered profiles exist → show profile picker / add-child flow
        if (unregisteredProfiles.length > 0) {
          if (res.data?.token) sessionStorage.setItem("newhubToken", res.data.token);
          if (res.data?.refresh_token) sessionStorage.setItem("newhubRefreshToken", res.data.refresh_token);
          setPendingVerifyOtpRes(res);
          setPfhUnregisteredProfiles(unregisteredProfiles);
          // Treat profiles with olympiad_year >= 2026 as already registered (not selectable)
          setPfhRegisteredProfiles(
            allProfiles.filter((p: any) => p.registration_completed || (p.olympiad_year != null && p.olympiad_year >= 2026))
          );
          setPfhTotalProfileCount(allProfiles.length);
          setIsOtpFieldVisible(false);
          setShowPfhSelectModal(true);
          return;
        }

        // PFH customer with 0 profiles → show new child modal directly
        // Skip if profileLinked is false (e.g. PHONE_NOT_FOUND_IN_SHOPIFY) — treat as normal checkout
        if (res.data?.is_pfh_customer && res.data?.profileLinked !== false) {
          if (res.data?.token) sessionStorage.setItem("newhubToken", res.data.token);
          if (res.data?.refresh_token) sessionStorage.setItem("newhubRefreshToken", res.data.refresh_token);
          setPendingVerifyOtpRes(res);
          // Pre-fill from response data
          setNewChild({
            name: res.data?.name ?? name,
            school: res.data?.school_name ?? "",
            schoolId: res.data?.school_id ?? null,
            grade: res.data?.grade ? String(res.data.grade) : "",
          });
          setSchoolQuery(res.data?.school_name ?? "");
          setIsOtpFieldVisible(false);
          setShowNewChildModal(true);
          return;
        }

        // Non-PFH, 0 profiles — new registration, will auto-login to stadia post-payment
        if (res.data?.token) sessionStorage.setItem("newhubToken", res.data.token);
        if (res.data?.refresh_token) sessionStorage.setItem("newhubRefreshToken", res.data.refresh_token);
        sessionStorage.setItem("stadiaAutoLogin", "true");

        // Capture CUID — brand-new user gets reserved_cuid
        if (res.data?.reserved_cuid) {
          persistCuid(res.data.reserved_cuid);
        }

        // Pre-fill form fields from response if not already set
        if (res.data?.name && !name) {
          setName(res.data.name);
          sessionStorage.setItem("name", res.data.name);
          userContext.setUserDetails((prev: any) => ({ ...prev, name: res.data.name }));
        }
        if (res.data?.pincode && !pincode) {
          setPincode(res.data.pincode);
          sessionStorage.setItem("pincode", res.data.pincode);
          fetchAddressFromPincode(res.data.pincode);
        }

        userContext.setIsOtpVerified(true);
        setIsOtpFieldVisible(false);
        showToast("Mobile number verified successfully!", "success");
        trackData("5678_checkout_otp_verified");
        ReactGA.event({ category: "Form_Completion", action: "5678_checkout_otp_verified", label: "Checkout_Form" });
      }

      if (!trackedFields.otp_verified) {
        setTrackedFields((prev) => ({ ...prev, otp_verified: true }));
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      showToast("Failed to verify OTP. Please try again.", "error");
      setErrors((prev) => ({ ...prev, otp: "Invalid OTP. Please try again." }));
    } finally {
      setIsLoading(false);
    }
  };

  // OTP handling logic
  const handleOtp = () => {
    if (!isOtpFieldVisible) {
      sendOtp();
    } else if (isResendEnabled) {
      handleResendOtp();
    }
  };

  // Resend OTP handler
  const handleResendOtp = () => {
    if (isResendEnabled) {
      sendOtp();
    }
  };

  // Update userDetails when local state changes
  useEffect(() => {
    try {
      userContext.setUserDetails((prevUserDetails: any) => ({
        ...prevUserDetails,
        name,
        email,
        pincode,
        grade,
      }));
      sessionStorage.setItem("name", name);
      sessionStorage.setItem("email", email);
      sessionStorage.setItem("pincode", pincode);
      if (grade) sessionStorage.setItem("grade", String(grade));
    } catch (error) {
      console.error("Error updating userDetails:", error);
      showToast("Failed to update form data. Please try again.", "error");
    }
  }, [name, email, pincode, grade, userContext.setUserDetails]);

  // Name change handler - matching original validation
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
    sessionStorage.setItem("name", value);

    // Validation
    const nameError = value.length < 3 ? "Please enter a valid name." : "";
    setErrors({ ...errors, name: nameError });

    // Update context
    userContext.setUserDetails((prevUserDetails: any) => ({
      ...prevUserDetails,
      name: value,
    }));

    // Track analytics
    if (value.length >= 3) {
      trackFieldCompletion("name", true);
    }
  };

  // Mobile change handler - matching original validation
  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Reset OTP state when mobile number changes
    if (value !== userContext.userDetails?.mobileNumber) {
      setOtp("");
      setIsOtpValid(false);
      setIsOtpFieldVisible(false);
      setIsResendEnabled(false);
      setResendTimer(30);
      userContext.setIsOtpVerified(false);
      userContext.setEarlyPricePhone("");
      setErrors((prev) => ({ ...prev, otp: "", sendOtpError: "" }));
    }

    // Validation
    const isValidMobile = userContext.backendSchool?.isInternational
      ? dubaiMobileValidation.test(value)
      : mobileValidation.test(value);
    const mobileError = isValidMobile
      ? ""
      : "Please enter a valid mobile number.";
    setErrors({ ...errors, mobileNumber: mobileError });

    // Keep raw input for display; store clean number in context/sessionStorage
    setRawMobileInput(value);
    const cleanPhone = normalisePhone(value);
    userContext.setUserDetails((prevUserDetails: any) => ({
      ...prevUserDetails,
      mobileNumber: cleanPhone,
    }));
    sessionStorage.setItem("mobileNumber", cleanPhone);

    // Track analytics
    if (isValidMobile) {
      trackFieldCompletion("mobile", true);
    }
  };

  // Pincode change handler
  const handlePincodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Only allow numeric input for pincode
    if (/\D/.test(value)) {
      return; // Exit if non-numeric input is detected
    }

    setPincode(value);
    sessionStorage.setItem("pincode", value);

    // Clear previous errors
    setErrors((prev) => ({ ...prev, pincode: "" }));

    // Validation
    if (value.length === 6) {
      fetchAddressFromPincode(value);
    } else if (value.length > 0) {
      setErrors((prev) => ({
        ...prev,
        pincode: "Pincode must be 6 digits",
      }));
    }

    // Update context
    userContext.setUserDetails((prevUserDetails: any) => ({
      ...prevUserDetails,
      pincode: value,
    }));
  };

  // Redirect to stadia pfh->nfo checkout for an existing (unregistered) pfh profile
  const redirectToStadiaForPfhProfile = (profile: any) => {
    // Persist this profile's CUID before leaving the page
    const selectedCuid: string = profile.cuid || profile.student_username || "";
    if (selectedCuid) persistCuid(selectedCuid);
    const token = pendingVerifyOtpRes?.data?.token ?? sessionStorage.getItem("newhubToken") ?? "";
    const refreshToken = pendingVerifyOtpRes?.data?.refresh_token ?? "";
    const phone = userContext.userDetails?.mobileNumber ?? "";
    const passwordSet = String(pendingVerifyOtpRes?.data?.password_set ?? false);
    const params = new URLSearchParams({
      studentUsername: profile.student_username,
      source: "pfh-nfo",
      branch: "same",
      token,
      ...(refreshToken ? { refreshToken } : {}),
      phone,
      passwordSet,
      childName: profile.name,
      childSchool: profile.school_name ?? "",
      childSchoolId: String(profile.school_id ?? ""),
      childGrade: String(profile.grade ?? ""),
    });
    trackData("5678_checkout_pfh_profile_selected");
    ReactGA.event({ category: "Registration", action: "5678_checkout_pfh_profile_selected", label: "PFH_Profile" });
    window.location.href = `https://stadia.nationalfinanceolympiad.com/checkout?${params.toString()}`;
  };

  // Add null check for userContext before rendering
  if (!userContext) {
    console.error("V2StudentDetails: userContext is null or undefined");
    return <div>Loading...</div>;
  }

  const mainContent = (
    <div className="v2-section">
      <div className="v2-section-header">
        <div className="v2-section-number">1</div>
        <h2 className="v2-section-title">Student Details</h2>
      </div>

      <div className="v2-form-grid">

        {/* Row 1: Mobile Number (with OTP) */}
        <div className="v2-form-row">
          <div className="v2-form-group">
            <label className="v2-label">
              {isPfhUser ? "Mobile Number *" : "Mobile Number *"}
            </label>
            {isPfhUser && (
              <div style={{ fontSize: "10px", color: "#666" }}>
                Enter your mobile number to instantly see the special discounts,
                rewards, and offers waiting just for you.
              </div>
            )}
            <input
              type="tel"
              className={`v2-input ${errors.mobileNumber ? "error" : ""}`}
              value={rawMobileInput || userContext.userDetails?.mobileNumber || ""}
              disabled={userContext.isOtpVerified}
              onChange={handleMobileChange}
              onBlur={(e) => {
                const value = e.target.value;
                const isValidMobile = userContext.backendSchool?.isInternational
                  ? dubaiMobileValidation.test(value)
                  : mobileValidation.test(value);
                if (!isValidMobile) {
                  setErrors({
                    ...errors,
                    mobileNumber: "Please enter a valid mobile number.",
                  });
                } else {
                  setErrors({ ...errors, mobileNumber: "" });
                }
              }}
              placeholder={
                errors.mobileNumber?.includes(PROFILE_LIMIT_MATCH)
                  ? "Use different mobile number"
                  : "Enter mobile number"
              }
            />
            {errors.mobileNumber && (
              <div className="v2-error-message" style={errors.mobileNumber.includes(PROFILE_LIMIT_MATCH) ? { display: "flex", alignItems: "center", gap: "6px" } : undefined}>
                {errors.mobileNumber.includes(PROFILE_LIMIT_MATCH) && (
                  <img src={MobileIcon} alt="" style={{ width: "18px", height: "18px", flexShrink: 0 }} />
                )}
                {errors.mobileNumber}
              </div>
            )}

            {/* OTP Verification - 4-digit individual inputs (nfo-stadia parity) */}
            {errors.sendOtpError && (
              <div className="v2-error-message" style={{ marginTop: "4px" }}>
                {errors.sendOtpError}
              </div>
            )}

            {!errors.mobileNumber &&
              userContext.userDetails?.mobileNumber &&
              !userContext.isOtpVerified && (
                <div style={{ marginTop: "8px", display: "flex", gap: "8px" }}>
                  {isOtpFieldVisible && (
                    <input
                      type="text"
                      className={`v2-input ${errors.otp ? "error" : ""}`}
                      value={otp}
                      disabled={isLoading}
                      onChange={(e) => {
                        const value = e.target.value;
                        setOtp(value);
                        setIsOtpValid(otpValidation.test(value));
                      }}
                      placeholder="4-digit OTP"
                      maxLength={4}
                      style={{ flex: "1" }}
                    />
                  )}
                  <button
                    className="v2-otp-button"
                    onClick={isOtpFieldVisible ? verifyOtp : handleOtp}
                    disabled={
                      isLoading ||
                      (isOtpFieldVisible && !isOtpValid) ||
                      (!isOtpFieldVisible &&
                        (!!errors.mobileNumber ||
                          !userContext.userDetails?.mobileNumber))
                    }
                    style={{
                      minWidth: isOtpFieldVisible ? "80px" : "100px",
                      flex: isOtpFieldVisible ? "0 0 auto" : "1",
                    }}
                  >
                    {isLoading ? (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" }}>
                        <div className="v2-loading-spinner"></div>
                        {isOtpFieldVisible ? "Verifying..." : "Sending..."}
                      </div>
                    ) : (
                      <>
                        {isOtpFieldVisible
                          ? "Verify"
                          : isPfhUser
                          ? "Check My Offers"
                          : "Send OTP"}
                      </>
                    )}
                  </button>
                </div>
              )}

            {isOtpFieldVisible && !isResendEnabled && (
              <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                Resend in {resendTimer}s
              </div>
            )}

            {isOtpFieldVisible && isResendEnabled && (
              <button
                onClick={handleOtp}
                disabled={isLoading}
                style={{
                  marginTop: "4px",
                  background: "none",
                  border: "none",
                  color: "#376CAA",
                  cursor: "pointer",
                  fontSize: "12px",
                  textDecoration: "underline",
                }}
              >
                Resend OTP
              </button>
            )}

            {errors.otp && (
              <div className="v2-error-message" style={{ marginTop: "4px" }}>
                {errors.otp}
              </div>
            )}

            {userContext.isOtpVerified && (
              <div
                className="v2-verification-success"
                style={{ fontSize: "12px" }}
              >
                {isPfhUser ? (
                  pfhVerificationFailed ? (
                    <span style={{ color: "#f59e0b" }}>
                      No offers available — standard pricing applies.
                    </span>
                  ) : (
                    <span style={{ color: "#10b981" }}>
                      ✓ Verified - Discount Applied
                    </span>
                  )
                ) : (
                  "✓ Mobile number verified"
                )}
              </div>
            )}
          </div>
        </div>

        {/* Row 2: Student Name — shown after OTP verified */}
        {userContext.isOtpVerified && (
          <div className="v2-form-row">
            <div className="v2-form-group">
              <label className="v2-label">Student Name *</label>
              <input
                type="text"
                className={`v2-input ${errors.name ? "error" : ""}`}
                value={name}
                onChange={handleNameChange}
                onBlur={(e) => {
                  const value = e.target.value;
                  if (value.length <= 0) {
                    setErrors({ ...errors, name: "Please enter a valid name." });
                  } else {
                    setErrors({ ...errors, name: "" });
                  }
                }}
                placeholder="Enter student name"
              />
              {errors.name && (
                <div className="v2-error-message">{errors.name}</div>
              )}
            </div>
          </div>
        )}

        {/* Row 3: Email — shown after OTP verified */}
        {userContext.isOtpVerified && (
          <div className="v2-form-row">
            <div className="v2-form-group">
              <label className="v2-label">Email *</label>
              <input
                type="email"
                className={`v2-input ${errors.email ? "error" : ""}`}
                value={email}
                onChange={(e) => {
                  const value = e.target.value;
                  setEmail(value);
                  sessionStorage.setItem("email", value);
                  userContext.setUserDetails((prev: any) => ({ ...prev, email: value }));
                  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
                  setErrors((prev) => ({ ...prev, email: emailValid || value === "" ? "" : "Please enter a valid email address." }));
                }}
                onBlur={(e) => {
                  const value = e.target.value;
                  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
                  setErrors((prev) => ({ ...prev, email: !value ? "Please enter your email address." : !emailValid ? "Please enter a valid email address." : "" }));
                  if (emailValid) { setEmailConfirmed(true); fireCheckoutInitiated(); }
                }}
                placeholder="Enter email address"
              />
              {errors.email && (
                <div className="v2-error-message">{errors.email}</div>
              )}
            </div>
          </div>
        )}

        {/* Row 4: Grade — shown after OTP verified */}
        {userContext.isOtpVerified && (
          <div className="v2-form-row">
            <div className="v2-form-group">
              <label className="v2-label">Grade *</label>
              <select
                className="v2-input"
                value={grade || ""}
                onChange={(e) => {
                  const g = parseInt(e.target.value, 10);
                  setGrade(isNaN(g) ? 0 : g);
                }}
              >
                <option value="">Select grade</option>
                {Array.from({ length: 8 }, (_, i) => i + 5).map((g) => (
                  <option key={g} value={g}>Grade {g}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Row 5: Pincode — shown after OTP verified */}
        {userContext.isOtpVerified && (
        <div className="v2-form-row">
          <div className="v2-form-group">
            <label className="v2-label">Pincode *</label>
            <div style={{ position: "relative" }}>
              <input
                type="text"
                className={`v2-input ${errors.pincode ? "error" : ""} ${
                  !userContext.individualReg ? "v2-input-disabled" : ""
                }`}
                value={pincode}
                disabled={pincodeLoading || !userContext.individualReg}
                onChange={handlePincodeChange}
                placeholder="Enter pincode"
                maxLength={6}
              />
              {pincodeLoading && (
                <div
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                  }}
                >
                  <div className="v2-loading-spinner"></div>
                  <span style={{ fontSize: "12px", color: "#666" }}>
                    Fetching...
                  </span>
                </div>
              )}
            </div>
            {pincodeLoading && (
              <div className="v2-loading-text" style={{ marginTop: "5px" }}>
                <span>Looking up city and state</span>
                <div className="v2-loading-dot">.</div>
                <div className="v2-loading-dot">.</div>
                <div className="v2-loading-dot">.</div>
              </div>
            )}
            {errors.pincode && (
              <div className="v2-error-message">{errors.pincode}</div>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );

  const modals = (
    <>
      {/* Second Student Modal — 1 completed registration, confirm before adding new child */}
      {showSecondStudentModal && (
        <div className="second-student-modal-overlay">
          <div className="second-student-modal">
            <div className="second-student-modal-mascot">
              <img
                src={require("../../../../Assets/svgs/pfh-choose-child-hero.webp")}
                alt="NFO Mascot"
                className="second-student-mascot-img"
              />
            </div>
            <p className="second-student-modal-title">Welcome to NFO.</p>
            <p className="second-student-modal-subtitle">
              You already have a student added to your account. Add another student as the second student?
            </p>
            <div className="second-student-existing-card">
              <div className="second-student-avatar">
                {existingStudentName.charAt(0).toUpperCase()}
              </div>
              <span className="second-student-name">{existingStudentName}</span>
            </div>
            <button
              className="second-student-add-btn"
              onClick={() => {
                setShowSecondStudentModal(false);
                setNewChild({ name, school: "", schoolId: null, grade: "" });
                setSchoolQuery("");
                setShowNewChildModal(true);
              }}
            >
              Add as second Student
            </button>
            <p className="second-student-modal-note">
              The student account will be linked to the same mobile number
            </p>
          </div>
          <button
            className="second-student-back-btn"
            onClick={() => {
              setShowSecondStudentModal(false);
              setPendingVerifyOtpRes(null);
              setExistingStudentName("");
            }}
          >
            Back
          </button>
        </div>
      )}

      {/* Profile Selection / Add Child Modal */}
      {showPfhSelectModal && (
        <div className="second-student-modal-overlay">
          <div className="second-student-modal">
            <div className="second-student-modal-mascot">
              <img
                src={require("../../../../Assets/svgs/pfh-choose-child-hero.webp")}
                alt="NFO Mascot"
                className="second-student-mascot-img"
              />
            </div>
            <p className="second-student-modal-title">Welcome to NFO!</p>
            <p className="second-student-modal-subtitle">
              {!canAddProfile || pfhTotalProfileCount >= 2
                ? "Select the child you'd like to register for NFO 2026-27."
                : "You have a child in your account. Register them for NFO or add a new child."}
            </p>

            <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
              {pfhUnregisteredProfiles.map((profile: any) => (
                <div
                  key={profile.student_username}
                  className="second-student-existing-card pfh-profile-selectable"
                  onClick={() => {
                    setShowPfhSelectModal(false);
                    redirectToStadiaForPfhProfile(profile);
                  }}
                >
                  <div className="second-student-avatar">
                    {profile.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <span className="second-student-name">{profile.name}</span>
                    {profile.grade && (
                      <span className="pfh-profile-grade">Grade {profile.grade}</span>
                    )}
                  </div>
                  <span className="pfh-profile-arrow">›</span>
                </div>
              ))}

              {pfhRegisteredProfiles.map((profile: any) => (
                <div
                  key={profile.student_username}
                  className="second-student-existing-card"
                  style={{ opacity: 0.6, cursor: "not-allowed" }}
                >
                  <div className="second-student-avatar" style={{ background: "#c6d9c6", color: "#2e6b2e" }}>
                    {profile.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <span className="second-student-name">{profile.name}</span>
                    {profile.grade && (
                      <span className="pfh-profile-grade">Grade {profile.grade}</span>
                    )}
                    <span style={{
                      display: "inline-block",
                      marginTop: "4px",
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "#2e6b2e",
                      background: "#d6f0d6",
                      borderRadius: "4px",
                      padding: "2px 7px",
                      fontFamily: "'OggTextMedium', sans-serif",
                    }}>
                      Registered for NFO 2026-27
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {canAddProfile && (
              <button
                className="second-student-add-btn pfh-outline-btn"
                onClick={() => {
                  setShowPfhSelectModal(false);
                  setNewChild({ name, school: "", schoolId: null, grade: "" });
                  setSchoolQuery("");
                  setShowNewChildModal(true);
                }}
              >
                + Add another child
              </button>
            )}

            <p className="second-student-modal-note">
              The student account will be linked to the same mobile number
            </p>
          </div>
          <button
            className="second-student-back-btn"
            onClick={() => {
              setShowPfhSelectModal(false);
              setPendingVerifyOtpRes(null);
              setPfhUnregisteredProfiles([]);
              setPfhRegisteredProfiles([]);
            }}
          >
            Back
          </button>
        </div>
      )}

      {showNewChildModal && (
        <div className="new-child-modal-overlay">
          <div className="new-child-modal">
            <div className="new-child-modal-header">
              <p className="new-child-modal-title">Enter your child's details</p>
              <p className="new-child-modal-subtitle">
                (this name comes on the issued certificate)
              </p>
            </div>

            <div className="new-child-form">
              {/* Student Name */}
              <div className="new-child-field">
                <label className="new-child-label">Student Name</label>
                <input
                  type="text"
                  className={`v2-input ${newChildErrors.name ? "error" : ""}`}
                  value={newChild.name}
                  placeholder="Full name"
                  onChange={(e) => setNewChild((c) => ({ ...c, name: e.target.value }))}
                />
                {newChildErrors.name && <div className="v2-error-message">{newChildErrors.name}</div>}
              </div>

              {/* School Name with search */}
              <div className="new-child-field" style={{ position: "relative" }}>
                <label className="new-child-label">School Name</label>
                <input
                  type="text"
                  className={`v2-input ${newChildErrors.school ? "error" : ""}`}
                  value={schoolQuery || newChild.school}
                  placeholder="Search school name"
                  onChange={async (e) => {
                    const q = e.target.value;
                    setSchoolQuery(q);
                    setNewChild((c) => ({ ...c, school: q, schoolId: null }));
                    if (q.trim().length >= 2) {
                      setSchoolSearchLoading(true);
                      setShowSchoolDropdown(false);
                      try {
                        const results = await searchSchools(q);
                        setSchoolResults(results);
                        setShowSchoolDropdown(results.length > 0);
                      } catch {
                        setSchoolResults([]);
                      } finally {
                        setSchoolSearchLoading(false);
                      }
                    } else {
                      setSchoolResults([]);
                      setShowSchoolDropdown(false);
                    }
                  }}
                  onFocus={() => { if (schoolResults.length > 0) setShowSchoolDropdown(true); }}
                  onBlur={() => setTimeout(() => setShowSchoolDropdown(false), 200)}
                  autoComplete="off"
                />
                {schoolSearchLoading && (
                  <div className="new-child-school-loading">Searching...</div>
                )}
                {showSchoolDropdown && (
                  <div className="new-child-school-dropdown">
                    {schoolResults.map((s) => (
                      <div
                        key={s.id}
                        className="new-child-school-option"
                        onMouseDown={() => {
                          setNewChild((c) => ({ ...c, school: s.name, schoolId: s.id }));
                          setSchoolQuery(s.name);
                          setShowSchoolDropdown(false);
                          setNewChildErrors((e) => ({ ...e, school: "" }));
                        }}
                      >
                        <span className="new-child-school-name">{s.name}</span>
                        {s.city && <span className="new-child-school-city">{s.city}, {s.state}</span>}
                      </div>
                    ))}
                  </div>
                )}
                {newChildErrors.school && <div className="v2-error-message">{newChildErrors.school}</div>}
              </div>

              {/* Grade */}
              <div className="new-child-field">
                <label className="new-child-label">Student Grade</label>
                <select
                  className={`v2-input new-child-select ${newChildErrors.grade ? "error" : ""}`}
                  value={newChild.grade}
                  onChange={(e) => {
                    setNewChild((c) => ({ ...c, grade: e.target.value }));
                    setNewChildErrors((e2) => ({ ...e2, grade: "" }));
                  }}
                >
                  <option value="">Select grade</option>
                  {Array.from({ length: 8 }, (_, i) => i + 5).map((g) => (
                    <option key={g} value={String(g)}>Grade {g}</option>
                  ))}
                </select>
                {newChildErrors.grade && <div className="v2-error-message">{newChildErrors.grade}</div>}
              </div>
            </div>

            <button
              className="second-student-add-btn"
              disabled={isAddingStudent}
              onClick={async () => {
                // Validate
                const errs = { name: "", school: "", grade: "" };
                if (!newChild.name.trim()) errs.name = "Please enter student name.";
                if (!newChild.school.trim()) errs.school = "Please enter school name.";
                if (!newChild.grade) errs.grade = "Please select a grade.";
                setNewChildErrors(errs);
                if (errs.name || errs.school || errs.grade) return;

                setIsAddingStudent(true);
                try {
                  const token = sessionStorage.getItem("newhubToken") ?? (pendingVerifyOtpRes?.data?.token ?? "");
                  const phone = userContext.userDetails?.mobileNumber ?? "";
                  const emailVal = pendingVerifyOtpRes?.data?.email ?? sessionStorage.getItem("email") ?? "";

                  const res = await addPfhStudent(
                    {
                      phone,
                      student_name: newChild.name,
                      school_name: newChild.school,
                      grade: Number(newChild.grade),
                      email: emailVal,
                    },
                    token
                  );

                  if (res.status !== "Success") {
                    showToast(res.message || "Failed to add student. Please try again.", "error");
                    return;
                  }

                  const studentUsername = res.data.student_username;
                  // student_username from add-pfh-student IS the CUID for this child
                  persistCuid(studentUsername);

                  trackData("5678_checkout_add_second_student");
                  ReactGA.event({ category: "Registration", action: "5678_checkout_add_second_student", label: "Second_Student" });

                  const refreshToken = pendingVerifyOtpRes?.data?.refresh_token ?? "";
                  const passwordSet = String(pendingVerifyOtpRes?.data?.password_set ?? false);
                  const params = new URLSearchParams({
                    studentUsername,
                    source: "pfh-nfo",
                    branch: "new",
                    token,
                    ...(refreshToken ? { refreshToken } : {}),
                    phone,
                    passwordSet,
                    childName: newChild.name,
                    childSchool: newChild.school,
                    childSchoolId: String(newChild.schoolId ?? ""),
                    childGrade: newChild.grade,
                  });
                  window.location.href = `https://stadia.nationalfinanceolympiad.com/checkout?${params.toString()}`;
                } catch (err: any) {
                  console.error("addPfhStudent error:", err);
                  // HTTP 400s (e.g. data.reason === "profile_limit_reached") carry a
                  // user-facing message in the body — show it instead of a generic error.
                  const backendMessage = err?.response?.data?.message;
                  showToast(
                    typeof backendMessage === "string" && backendMessage
                      ? backendMessage
                      : "Something went wrong. Please try again.",
                    "error"
                  );
                } finally {
                  setIsAddingStudent(false);
                }
              }}
            >
              {isAddingStudent ? "Please wait..." : "Continue to payment"}
            </button>
          </div>
          <button
            className="second-student-back-btn"
            onClick={() => {
              setShowNewChildModal(false);
              setNewChild({ name: "", school: "", schoolId: null, grade: "" });
              setNewChildErrors({ name: "", school: "", grade: "" });
              setSchoolQuery("");
              if (pfhUnregisteredProfiles.length > 0) {
                setShowPfhSelectModal(true);
              } else if (existingStudentName) {
                setShowSecondStudentModal(true);
              } else {
                setPendingVerifyOtpRes(null);
              }
            }}
          >
            Back
          </button>
        </div>
      )}

      {/* Re-register returning student modal */}
      {showReregisterModal && (
        <div className="second-student-modal-overlay">
          <div className="second-student-modal">
            <div className="second-student-modal-mascot">
              <img
                src={require("../../../../Assets/svgs/pfh-choose-child-hero.webp")}
                alt="NFO Mascot"
                className="second-student-mascot-img"
              />
            </div>
            <p className="second-student-modal-title">Welcome back!</p>
            <p className="second-student-modal-subtitle">
              {reregisterCandidates.length === 1
                ? "Register the same student for NFO 2026-27?"
                : "Which student would you like to register for NFO 2026-27?"}
            </p>

            {reregisterCandidates.map((profile: any) => (
              <div
                key={profile.student_username}
                className="second-student-existing-card"
                style={{ flexDirection: "column", alignItems: "flex-start", gap: "4px", padding: "12px 16px" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%" }}>
                  <div className="second-student-avatar">
                    {profile.name?.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="second-student-name">{profile.name}</div>
                    <div style={{ fontSize: "13px", color: "#666", marginTop: "2px" }}>
                      Grade {profile.grade} · {profile.school_name}
                    </div>
                  </div>
                  {reregisterCandidates.length > 1 && (
                    <button
                      className="second-student-add-btn"
                      style={{ margin: 0, padding: "8px 16px", fontSize: "13px" }}
                      onClick={() => {
                        setShowReregisterModal(false);
                        setName(profile.name ?? "");
                        sessionStorage.setItem("name", profile.name ?? "");
                        userContext.setUserDetails((prev: any) => ({ ...prev, name: profile.name ?? "" }));
                        const gradeNum = Number(profile.grade);
                        if (gradeNum) {
                          setGrade(gradeNum);
                          sessionStorage.setItem("grade", String(gradeNum));
                          userContext.setUserDetails((prev: any) => ({ ...prev, grade: String(gradeNum) }));
                        }
                        sessionStorage.setItem("rollNumber", profile.student_username ?? "");
                        sessionStorage.setItem("stadiaAutoLogin", "true");
                        if (pendingVerifyOtpRes?.data?.reserved_cuid) {
                          persistCuid(pendingVerifyOtpRes.data.reserved_cuid);
                        }
                        userContext.setIsOtpVerified(true);
                        showToast("Mobile number verified successfully!", "success");
                        ReactGA.event({ category: "Form_Completion", action: "5678_checkout_otp_verified", label: "Checkout_Form" });
                      }}
                    >
                      Register
                    </button>
                  )}
                </div>
              </div>
            ))}

            {reregisterCandidates.length === 1 && (
              <button
                className="second-student-add-btn"
                onClick={() => {
                  const profile = reregisterCandidates[0];
                  setShowReregisterModal(false);
                  setName(profile.name ?? "");
                  sessionStorage.setItem("name", profile.name ?? "");
                  userContext.setUserDetails((prev: any) => ({ ...prev, name: profile.name ?? "" }));
                  const gradeNum = Number(profile.grade);
                  if (gradeNum) {
                    setGrade(gradeNum);
                    sessionStorage.setItem("grade", String(gradeNum));
                    userContext.setUserDetails((prev: any) => ({ ...prev, grade: String(gradeNum) }));
                  }
                  sessionStorage.setItem("rollNumber", profile.student_username ?? "");
                  sessionStorage.setItem("stadiaAutoLogin", "true");
                  if (pendingVerifyOtpRes?.data?.reserved_cuid) {
                    persistCuid(pendingVerifyOtpRes.data.reserved_cuid);
                  }
                  userContext.setIsOtpVerified(true);
                  showToast("Mobile number verified successfully!", "success");
                  ReactGA.event({ category: "Form_Completion", action: "5678_checkout_otp_verified", label: "Checkout_Form" });
                }}
              >
                Yes, register {reregisterCandidates[0]?.name?.split(" ")[0]}
              </button>
            )}

            {/* Creates a new profile → hidden once the household is at the 2-profile cap. */}
            {canAddProfile && (
            <button
              className="second-student-back-btn"
              style={{ position: "static", marginTop: "12px" }}
              onClick={() => {
                setShowReregisterModal(false);
                setReregisterCandidates([]);
                const res = pendingVerifyOtpRes;
                setPendingVerifyOtpRes(null);
                if (res?.data?.token) sessionStorage.setItem("newhubToken", res.data.token);
                if (res?.data?.refresh_token) sessionStorage.setItem("newhubRefreshToken", res.data.refresh_token);
                sessionStorage.setItem("stadiaAutoLogin", "true");
                if (res?.data?.reserved_cuid) persistCuid(res.data.reserved_cuid);
                userContext.setIsOtpVerified(true);
                showToast("Mobile number verified successfully!", "success");
                ReactGA.event({ category: "Form_Completion", action: "5678_checkout_otp_verified", label: "Checkout_Form" });
              }}
            >
              Register a new student
            </button>
            )}
          </div>
        </div>
      )}

      {/* PFH Success Popup */}
      {showPfhSuccessPopup && (
        <div className="pfh-success-popup-overlay">
          <div className="pfh-success-popup">
            <div className="pfh-success-popup-header">
              <h3>🎉 Congratulations!</h3>
            </div>
            <div className="pfh-success-popup-body">
              <p>
                {pfh2025BookOffer
                  ? "Your verification was successful! As a Personal Finance Handbook customer, your registration comes with a free physical guidebook."
                  : "Your verification was successful! You're eligible for exclusive discounts and special pricing."}
              </p>
              <div className="pfh-success-benefits">
                <div className="benefit-item">
                  <span className="benefit-icon">{pfh2025BookOffer ? "📚" : "💰"}</span>
                  <span>
                    {pfh2025BookOffer
                      ? "Free physical guidebook — we'll ask for your shipping address after registration"
                      : "Special discount pricing applied"}
                  </span>
                </div>
              </div>
            </div>
            <div className="pfh-success-popup-footer">
              <button
                className="pfh-popup-continue-btn"
                onClick={() => {
                  setShowPfhSuccessPopup(false);
                  trackData("5678_pfh_success_popup_continue");
                  ReactGA.event({
                    category: "PFH_Verification",
                    action: "5678_pfh_success_popup_continue",
                    label: "Discount_Flow",
                  });
                }}
              >
                Continue to Checkout
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <>
      {mainContent}
      {ReactDOM.createPortal(modals, document.body)}
    </>
  );
};

export default V2StudentDetails;

